import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { vueI18n, getSupportedLocales, fallbackLocale, getLocale } from '../helpers/I18nPluginHelper.js';

Vue.use(VueI18n);

const loadedLocales = new Set();
const pendingLocaleLoads = new Map();
let i18n = null;

function isLocalI18nCachingDisabled() {
    const runtimeEnv = (typeof window !== `undefined` && window.env && window.env.VUE_APP_ENV)
        ? window.env.VUE_APP_ENV
        : (process.env.VUE_APP_ENV || process.env.SERVER_ENV || ``);

    return String(runtimeEnv).toLowerCase() === `local`;
}

function normalizeSupportedLocale(locale) {
    if (!locale) return null;
    const supportedCodes = getSupportedLocales()?.map(l => l.locale) || [];
    const lower = String(locale).toLowerCase();
    const exact = supportedCodes.find(code => code.toLowerCase() === lower);
    if (exact) return exact;

    const short = lower.split(`-`)[0];
    return supportedCodes.find(code => code.toLowerCase() === short) || null;
}

function loadLocaleMessages(locale) {
    const normalized = normalizeSupportedLocale(locale);
    if (!normalized) {
        return Promise.resolve(null);
    }
    const disableCache = isLocalI18nCachingDisabled();

    if (!disableCache && loadedLocales.has(normalized)) {
        return Promise.resolve(normalized);
    }
    if (pendingLocaleLoads.has(normalized)) {
        return pendingLocaleLoads.get(normalized);
    }

    const loader = import(`../../src/assets/i18n/${normalized}/map.json`).then((messageModule) => {
        if (!i18n) {
            return normalized;
        }
        const messageValue = messageModule && messageModule.default ? messageModule.default : messageModule;
        i18n.setLocaleMessage(normalized, { map: messageValue });
        if (!disableCache) {
            loadedLocales.add(normalized);
        }
        return normalized;
    }).catch((error) => {
        console.warn(`Failed to load locale messages for ${normalized}`, error);
        return null;
    }).finally(() => {
        pendingLocaleLoads.delete(normalized);
    });

    pendingLocaleLoads.set(normalized, loader);
    return loader;
}

function ensureLocaleMessages(locale) {
    const normalized = normalizeSupportedLocale(locale);
    const fallback = normalizeSupportedLocale(fallbackLocale);

    const tasks = [];
    if (normalized) {
        tasks.push(loadLocaleMessages(normalized));
    }
    if (fallback && fallback !== normalized) {
        tasks.push(loadLocaleMessages(fallback));
    }

    return Promise.all(tasks);
}

export function loadLanguageAsync(locale) {
    const normalized = normalizeSupportedLocale(locale) || normalizeSupportedLocale(fallbackLocale);
    if (!normalized) {
        return Promise.resolve(null);
    }

    if (i18n && i18n.locale === normalized && loadedLocales.has(normalized)) {
        return Promise.resolve(normalized);
    }

    return ensureLocaleMessages(normalized).then(() => {
        if (i18n) {
            i18n.locale = normalized;
        }
        return normalized;
    });
}

const initialLocale = getLocale();
if (typeof window !== `undefined`) {
    console.log(`Setting initial locale to: `, initialLocale);
    localStorage.setItem(`previewLanguage`, initialLocale);
}

i18n = new VueI18n({
    locale: initialLocale,
    fallbackLocale: fallbackLocale,
    messages: {}
});

vueI18n.instance = i18n;
vueI18n.ensureLocaleMessages = ensureLocaleMessages;
vueI18n.loadLanguageAsync = loadLanguageAsync;

export default i18n;
